import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import SEO from "../components/SEO.jsx"; 
import { apiFetch } from "../lib/api.js";

const css = `
:root {
  --blog-width: 740px;
  --blog-sidebar: 280px;
  --ps-accent:#6E5BFF;
  --ps-accent-2:#A78BFA;
  --ps-black:#0b0b0c;
  --ps-muted:#6b7280;
  --nav-h: 56px;
}

body { overflow: hidden; }

/* Custom Scrollbar + Layout */
.page-scroll {
  height: calc(100vh - var(--nav-h));
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #a78bfa #eef1ff;
  display: flex;
  flex-direction: column;
}
.page-scroll::-webkit-scrollbar { width: 10px; }
.page-scroll::-webkit-scrollbar-track { background:#eef1ff; border-left:1px solid rgba(17,24,39,.08); }
.page-scroll::-webkit-scrollbar-thumb { background:linear-gradient(180deg,#c7d2fe,#a78bfa); border:2px solid #eef1ff; border-radius:8px; }

/* Content Wrapper (flex-grow) */
.blog-wrap { 
  flex: 1 0 auto; /* Roztáhne se a odtlačí footer */
  background: #fff; 
  padding-bottom: 80px; 
}

.blog-header { background: radial-gradient(1200px 600px at 50% -10%, #f4f6ff, #f7f9fc 40%, #fff); padding: 80px 20px 60px; text-align: center; border-bottom: 1px solid #e5e7eb; }
.blog-container { max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: var(--blog-sidebar) 1fr; gap: 60px; padding: 40px 20px; }
.blog-content { max-width: var(--blog-width); font-size: 18px; line-height: 1.75; color: #1f2937; }
.blog-sidebar { position: sticky; top: 40px; height: fit-content; }
.blog-title { font-size: 48px; font-weight: 800; color: #111827; margin: 16px 0; }
.blog-badge { background: rgba(110,91,255,0.1); color: var(--ps-accent); font-size: 12px; font-weight: 700; padding: 4px 12px; border-radius: 99px; }

@media (max-width: 900px) {
  .blog-container { grid-template-columns: 1fr; }
  .blog-sidebar { display: none; }
}

/* Footer Styles */
.ps-footer {
  flex-shrink: 0;
  border-top: 1px solid rgba(148,163,184,0.35);
  padding: 30px 20px;
  background: #fff;
  margin-top: auto; /* Pojistka */
}
.footer-container { max-width: 1200px; margin: 0 auto; }
.ps-footer-inner { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.ps-footer-brand { display: flex; align-items: center; gap: 10px; }
.ps-footer-logo { width: 26px; height: 26px; display: inline-flex; align-items: center; justify-content: center; }
.ps-footer-title { font-weight: 700; font-size: 15px; color: var(--ps-black); }
.ps-footer-brand p { margin: 0; font-size: 13px; color: #9ca3af; }
.ps-footer-links { display: flex; flex-wrap: wrap; gap: 10px; font-size: 13px; }
.ps-footer-links a { text-decoration: none; color: #6b7280; }
.ps-footer-links a:hover { color: var(--ps-black); text-decoration: underline; }
.ps-footer-bottom { margin-top: 12px; font-size: 12px; color: #9ca3af; }
`;

export default function BlogPillar() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    const loadPost = async () => {
      try {
        setLoading(true);
        const data = await apiFetch(`/api/blog/posts/${slug}`);
        setPost(data.item);
      } catch (err) {
        console.error(err);
        setError("Post not found");
      } finally {
        setLoading(false);
      }
    };
    loadPost();
  }, [slug]);

  if (loading) return <div style={{padding:'100px', textAlign:'center'}}>Loading...</div>;
  if (error || !post) return <div style={{padding:'100px', textAlign:'center'}}>404 - Post not found</div>;

  const renderBlock = (block, index) => {
    switch (block.type) {
      case "h2":
        return <h2 key={index} id={`section-${index}`} style={{fontSize:'30px', fontWeight:'700', marginTop:'48px'}}>{block.text}</h2>;
      case "p":
        return <p key={index} style={{marginBottom:'24px'}}>{block.text}</p>;
      case "blockquote":
        return <blockquote key={index} style={{borderLeft:'4px solid #6E5BFF', background:'#f9fafb', padding:'20px', margin:'32px 0', fontStyle:'italic'}}>{block.text}</blockquote>;
      case "ul":
        return (
          <ul key={index} style={{paddingLeft:'24px', marginBottom:'24px'}}>
            {(block.items || []).map((item, i) => (
               <li key={i} style={{marginBottom:'8px'}}>{item}</li>
            ))}
          </ul>
        );
      case "cta":
        return (
          <div key={index} style={{background:'#111827', color:'#fff', padding:'40px', borderRadius:'20px', textAlign:'center', margin:'60px 0'}}>
            <h3 style={{color:'#fff', marginTop:0}}>{block.title}</h3>
            <p style={{marginBottom:'24px', opacity:0.9}}>{block.text}</p>
            <Link to={block.link} style={{background:'#6E5BFF', color:'#fff', padding:'14px 28px', borderRadius:'99px', textDecoration:'none', fontWeight:'700'}}>{block.btnText}</Link>
          </div>
        );
      default: return null;
    }
  };

  return (
    <>
      <SEO title={post.title} description={post.excerpt} />
      <style>{css}</style>
      
      <div className="page-scroll">
        
        {/* Main Content */}
        <div className="blog-wrap">
          <header className="blog-header">
            <span className="blog-badge">{post.category}</span>
            <h1 className="blog-title">{post.title}</h1>
            <p style={{color:'#6b7280'}}>By {post.author} • {new Date(post.updated_at).toLocaleDateString()}</p>
          </header>

          <div className="blog-container">
            <aside className="blog-sidebar">
               <div style={{fontWeight:'700', color:'#9ca3af', marginBottom:'12px', fontSize:'12px', textTransform:'uppercase'}}>Contents</div>
               <ul style={{listStyle:'none', padding:0}}>
                 {post.content.map((b, i) => b.type === 'h2' ? (
                   <li key={i} style={{marginBottom:'10px'}}>
                     <a href={`#section-${i}`} style={{textDecoration:'none', color:'#6b7280', fontSize:'14px'}}>{b.text}</a>
                   </li>
                 ) : null)}
               </ul>
            </aside>

            <article className="blog-content">
              {post.content.map((block, index) => renderBlock(block, index))}
            </article>
          </div>
        </div>

        {/* Footer */}
        <footer className="ps-footer">
          <div className="footer-container">
            <div className="ps-footer-inner">
              <div className="ps-footer-brand">
                <span className="ps-footer-logo" aria-hidden>
                  <img className="brand-icon" src="https://www.musicbase.io/logo.png" alt="" aria-hidden style={{ height: "25px", width: "25px" }} />
                </span>
                <div>
                  <div className="ps-footer-title">MusicBase</div>
                  <p>Playlist outreach built for artists, not sales teams.</p>
                </div>
              </div>

              <nav className="ps-footer-links" aria-label="Footer">
                <a href="/">Home</a>
                <a href="/pricing">Pricing</a>
                <a href="/login">Log in</a>
                <a href="/signup">Sign up</a>
                <a href="/contact">Contact</a>
                <a href="/terms">Terms</a>
                <a href="/privacy">Privacy Policy</a>
              </nav>
            </div>
            <div className="ps-footer-bottom">
              © {new Date().getFullYear()} MusicBase. All rights reserved.
            </div>
          </div>
        </footer>

      </div>
    </>
  );
}